// Clock and Coins: read the clock, count the money, make change. Four choices each time.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import type { GameModule, GameContext } from '@shell/types';
import { makeClockCoins, COIN_VALUE, fmtMoney, type CCProblem, type CoinKind } from '@problems/clock-coins';

const PROBLEMS = 8;
const COIN_STYLE: Record<CoinKind, { fill: string; ink: string; label: string }> = {
  p: { fill: '#c0603a', ink: '#fff', label: '1¢' },
  n: { fill: '#b9b9c0', ink: '#2a2a2e', label: '5¢' },
  d: { fill: '#d6d6dc', ink: '#2a2a2e', label: '10¢' },
  q: { fill: '#c9c9d0', ink: '#2a2a2e', label: '25¢' },
  h: { fill: '#bfbfc8', ink: '#2a2a2e', label: '50¢' },
  b: { fill: '#5fbf6a', ink: '#f6f3ec', label: '$1' },
};

function mount(host: HTMLElement, ctx: GameContext) {
  let n = 0;
  let prob!: CCProblem;
  let misses = 0, hinted = false;
  let attempts = 0, correct = 0;
  let level = ctx.currentLevel();
  let maxLevel = level;
  let locked = false;
  let sawChange = false;
  const t0 = performance.now();
  let tProblem = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Problem ', h('strong', null, '1'), ` of ${PROBLEMS}`), h('span.muted.small', null, ''));
  const stage = h('div');
  const question = h('div.prompt', { style: { fontSize: '20px', minHeight: '56px' } });
  const choices = h('div.choices');
  const help = h('p.center.muted.small', { style: { minHeight: '20px' } }, '');
  host.append(head, stage, question, choices, help);

  const S = makeSurface(stage, { aspect: 0.62, draw: false, onResize: () => draw() });

  function newProblem() {
    level = ctx.currentLevel();
    maxLevel = Math.max(maxLevel, level);
    prob = makeClockCoins(level, ctx.rng, n);
    if (prob.kind === 'change') sawChange = true;
    misses = 0; hinted = false; locked = false;
    tProblem = performance.now();
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    (head.lastElementChild as HTMLElement).textContent = prob.kind === 'clock' || prob.kind === 'elapsed' ? 'Clock' : 'Coins';
    question.textContent = prob.question;
    help.textContent = '';
    clear(choices);
    for (const ch of prob.choices) {
      choices.append(h('button.choice', { onclick: (e: Event) => answer(ch, e.currentTarget as HTMLElement) }, ch));
    }
    draw();
  }

  function answer(ch: string, btn: HTMLElement) {
    if (locked) return;
    const ms = performance.now() - tProblem;
    if (ch === prob.answer) {
      locked = true;
      attempts++; correct++;
      ctx.report({ skill: 'measure_time_money', level, correct: true, hintUsed: hinted, ms, problemId: `cc:${prob.kind}:${prob.answer}` });
      ctx.sound('ding');
      btn.classList.add('good');
      ctx.say(prob.kind === 'clock' ? `Yes, it is ${prob.answer}.` : prob.kind === 'elapsed' ? `Right, ${prob.answer}.` : `Yes, ${prob.answer}.`);
      next();
    } else {
      misses++;
      if (misses === 1) { attempts++; ctx.report({ skill: 'measure_time_money', level, correct: false, hintUsed: hinted, ms, problemId: `cc:${prob.kind}:${prob.answer}` }); }
      ctx.sound('buzz');
      btn.classList.add('bad');
      setTimeout(() => btn.classList.remove('bad'), 500);
      if (misses >= 2) {
        locked = true;
        ctx.say(`It was ${prob.answer}. ${prob.hint}`);
        for (const b of Array.from(choices.children) as HTMLElement[]) if (b.textContent === prob.answer) b.classList.add('good');
        next(1800);
      } else {
        hinted = true;
        help.textContent = `Hint: ${prob.hint}`;
        ctx.say(`Not ${ch}. Have another look.`);
      }
    }
  }

  function next(delay = 900) {
    n++;
    if (n >= PROBLEMS) {
      if (attempts === correct && sawChange && maxLevel >= 5) ctx.charm('charm.change');
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), delay);
      return;
    }
    setTimeout(newProblem, delay);
  }

  function draw() {
    if (!prob || !S.w) return;
    const c = S.ctx, W = S.w, H = S.h;
    c.fillStyle = '#efe9db'; c.fillRect(0, 0, W, H);
    if (prob.kind === 'clock' || prob.kind === 'elapsed') drawClock(c, W / 2, H / 2, Math.min(W, H) * 0.44, prob.h, prob.m);
    else if (prob.kind === 'coins') drawCoins(c, W, H, prob.coins);
    else drawChange(c, W, H, prob.cost, prob.paid);
  }

  function drawClock(c: CanvasRenderingContext2D, cx: number, cy: number, r: number, hr: number, mn: number) {
    c.fillStyle = '#fff'; c.beginPath(); c.arc(cx, cy, r, 0, 7); c.fill();
    c.strokeStyle = '#2a2a2e'; c.lineWidth = Math.max(3, r * 0.05); c.stroke();
    for (let i = 0; i < 60; i++) {
      const a = (i / 60) * Math.PI * 2 - Math.PI / 2;
      const big = i % 5 === 0;
      c.strokeStyle = big ? '#2a2a2e' : '#b4b3a8'; c.lineWidth = big ? 3 : 1.5;
      c.beginPath(); c.moveTo(cx + Math.cos(a) * r * (big ? 0.84 : 0.9), cy + Math.sin(a) * r * (big ? 0.84 : 0.9)); c.lineTo(cx + Math.cos(a) * r * 0.95, cy + Math.sin(a) * r * 0.95); c.stroke();
    }
    c.fillStyle = '#2a2a2e'; c.font = `800 ${Math.round(r * 0.18)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
    for (let i = 1; i <= 12; i++) { const a = (i / 12) * Math.PI * 2 - Math.PI / 2; c.fillText(String(i), cx + Math.cos(a) * r * 0.7, cy + Math.sin(a) * r * 0.7); }
    const ma = (mn / 60) * Math.PI * 2 - Math.PI / 2;
    const ha = ((hr % 12) / 12 + mn / 720) * Math.PI * 2 - Math.PI / 2;
    c.lineCap = 'round';
    c.strokeStyle = '#2a2a2e'; c.lineWidth = Math.max(5, r * 0.08);
    c.beginPath(); c.moveTo(cx, cy); c.lineTo(cx + Math.cos(ha) * r * 0.5, cy + Math.sin(ha) * r * 0.5); c.stroke();
    c.strokeStyle = '#4f8fe6'; c.lineWidth = Math.max(3, r * 0.05);
    c.beginPath(); c.moveTo(cx, cy); c.lineTo(cx + Math.cos(ma) * r * 0.78, cy + Math.sin(ma) * r * 0.78); c.stroke();
    c.fillStyle = '#e2574c'; c.beginPath(); c.arc(cx, cy, Math.max(4, r * 0.06), 0, 7); c.fill();
  }

  function drawCoin(c: CanvasRenderingContext2D, kind: CoinKind, x: number, y: number, r: number) {
    const st = COIN_STYLE[kind];
    if (kind === 'b') {
      c.fillStyle = st.fill; c.fillRect(x - r * 1.6, y - r * 0.75, r * 3.2, r * 1.5);
      c.strokeStyle = '#2e7d4f'; c.lineWidth = 2; c.strokeRect(x - r * 1.4, y - r * 0.55, r * 2.8, r * 1.1);
    } else {
      c.fillStyle = st.fill; c.beginPath(); c.arc(x, y, r, 0, 7); c.fill();
      c.strokeStyle = 'rgba(0,0,0,.25)'; c.lineWidth = 2; c.stroke();
    }
    c.fillStyle = st.ink; c.font = `900 ${Math.round(r * 0.6)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
    c.fillText(st.label, x, y + 1);
  }

  function drawCoins(c: CanvasRenderingContext2D, W: number, H: number, coins: CoinKind[]) {
    const n = coins.length;
    const perRow = n <= 4 ? n : Math.ceil(n / 2);
    const rows = n <= 4 ? 1 : 2;
    const r = Math.min(W / (perRow * 2.6), H / (rows * 2.6), 34);
    coins.forEach((k, i) => {
      const row = Math.floor(i / perRow), col = i % perRow;
      const inRow = row === rows - 1 ? n - perRow * (rows - 1) : perRow;
      const x = W / 2 + (col - (inRow - 1) / 2) * r * 2.5;
      const y = H / 2 + (row - (rows - 1) / 2) * r * 2.4;
      drawCoin(c, k, x, y, k === 'q' || k === 'h' ? r : k === 'd' ? r * 0.8 : k === 'p' ? r * 0.85 : r * 0.9);
    });
  }

  function drawChange(c: CanvasRenderingContext2D, W: number, H: number, cost: number, paid: number) {
    // price tag and the money handed over
    c.fillStyle = '#f5c542';
    c.beginPath(); c.moveTo(W * 0.1, H * 0.3); c.lineTo(W * 0.42, H * 0.3); c.lineTo(W * 0.42, H * 0.7); c.lineTo(W * 0.1, H * 0.7); c.lineTo(W * 0.04, H * 0.5); c.closePath(); c.fill();
    c.fillStyle = '#2a2a2e'; c.beginPath(); c.arc(W * 0.1, H * 0.5, 5, 0, 7); c.fill();
    c.font = `900 ${Math.round(H * 0.16)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
    c.fillText(fmtMoney(cost), W * 0.26, H * 0.5);
    c.fillStyle = '#2a2a2e'; c.font = `800 ${Math.round(H * 0.11)}px Nunito, sans-serif`; c.fillText('you pay', W * 0.72, H * 0.22);
    if (paid >= 100) {
      const bills = Math.round(paid / 100);
      for (let i = 0; i < Math.min(bills, 5); i++) drawCoin(c, 'b', W * 0.72 + i * 6, H * 0.55 + i * 6, Math.min(W * 0.07, H * 0.14));
      if (bills > 1) { c.fillStyle = '#2a2a2e'; c.font = `900 ${Math.round(H * 0.14)}px Nunito, sans-serif`; c.fillText(fmtMoney(paid), W * 0.72, H * 0.86); }
    }
    void COIN_VALUE;
  }

  newProblem();
  return { destroy() { S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'math.clock-coins',
  title: 'Clock and Coins',
  blurb: 'Read the clock, count the coins, and make change like a shopkeeper.',
  category: 'math',
  skills: ['measure_time_money'],
  levelRange: [1, 10],
  unlock: { stars: 12 },
  mount,
};
export default mod;
